import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { X, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ onClose }) => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotEmailSent, setForgotEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, { email, password });
      
      login(response.data.token, response.data.email, response.data.is_admin);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotEmailSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setIsForgotPassword(false);
    setForgotEmailSent(false);
    setIsLogin(true);
  };

  // Forgot Password View
  if (isForgotPassword) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl border-2 border-black hard-shadow-lg max-w-md w-full p-8 relative">
          <button
            onClick={onClose}
            data-testid="auth-modal-close"
            className="absolute top-4 right-4 p-2 hover:bg-[#F0F0F0] rounded-full transition-all"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>

          <button
            onClick={resetToLogin}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-black mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            Reset Password
          </h2>
          
          {forgotEmailSent ? (
            <div className="text-center py-6">
              <div className="text-6xl mb-4">📧</div>
              <p className="text-gray-600 mb-4">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Check your inbox and spam folder. The link expires in 1 hour.
              </p>
              <button
                onClick={resetToLogin}
                className="bg-[#FF6B6B] text-white font-bold py-2 px-6 rounded-full border-2 border-black hard-shadow-hover"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    required
                    className="w-full border-2 border-black rounded-lg p-3 focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]"
                    placeholder="your@email.com"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={loading}
                  className="w-full bg-[#FF6B6B] text-white font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  // Login/Register View
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border-2 border-black hard-shadow-lg max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          data-testid="auth-modal-close"
          className="absolute top-4 right-4 p-2 hover:bg-[#F0F0F0] rounded-full transition-all"
        >
          <X className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          {isLogin ? t('auth.welcomeBack') : t('auth.joinUs')}
        </h2>
        <p className="text-gray-600 mb-6">
          {isLogin ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
              required
              className="w-full border-2 border-black rounded-lg p-3 focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password-input"
              required
              className="w-full border-2 border-black rounded-lg p-3 focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]"
              placeholder="••••••••"
            />
          </div>

          {isLogin && (
            <button
              type="button"
              onClick={() => setIsForgotPassword(true)}
              data-testid="forgot-password-link"
              className="text-sm text-[#FF6B6B] hover:underline font-medium"
            >
              {t('auth.forgot')}
            </button>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full bg-[#FF6B6B] text-white font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Please wait...
              </>
            ) : (
              isLogin ? t('auth.login') : t('auth.signUp')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            data-testid="auth-toggle-button"
            className="text-sm text-gray-600 hover:text-black font-bold transition-all"
          >
            {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
